import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Typography,
} from '@mui/material';
import { Delete, Add } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { dealersApi } from '../../../api/dealers.api';
import { evcpdProductsApi } from '../../../api/evcpdProducts.api';
import { clientPortalApi } from '../../../api/clientPortal.api';
import type { CreateOrderInput } from '../../../api/dealerOrders.api';
import { useAuth } from '../../../hooks/useAuth';
import { DealerSummaryCard } from '../../../components/DealerSummaryCard';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateOrderInput) => Promise<void>;
  error?: string | null;
}

interface Line { productId: number; quantity: number }

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

export function OrderFormDialog({ open, onClose, onSubmit, error }: Props) {
  const { user } = useAuth();
  const isDealer = user?.roles.includes('dealer');
  const isClient = user?.linkedEntityType === 'client';
  const [dealerId, setDealerId] = useState<number | ''>('');
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [productId, setProductId] = useState<number | ''>('');
  const [qty, setQty] = useState('1');

  // Staff/dealers pick from the full approved-dealer list; a CLIENT can only order for THEIR mapped
  // dealers (clients have no dealer.read), so they come from the scoped client-portal endpoint.
  const dealers = useQuery({
    queryKey: ['dealers', 'approved-picker'],
    queryFn: () => dealersApi.list({ status: 'approved', limit: 100 }),
    enabled: open && !isClient,
  });
  const clientDealers = useQuery({
    queryKey: ['client-portal', 'dealers'],
    queryFn: () => clientPortalApi.dealers(),
    enabled: open && Boolean(isClient),
  });
  const dealerOptions = isClient
    ? (clientDealers.data ?? []).map((d) => ({ id: d.dealer_id, dealerName: d.dealer_name, dealerCode: d.dealer_code }))
    : (dealers.data?.data ?? []).map((d) => ({ id: d.id, dealerName: d.dealerName, dealerCode: d.dealerCode }));
  const products = useQuery({
    queryKey: ['evcpd-products', 'picker'],
    queryFn: () => evcpdProductsApi.list({ status: 'active', limit: 200 }),
    enabled: open,
  });

  useEffect(() => {
    if (!open) { setDealerId(''); setRemarks(''); setLines([]); setProductId(''); setQty('1'); }
  }, [open]);

  // Dealer (own record) or a client with a single mapped dealer — auto-select it.
  useEffect(() => {
    if (open && (isDealer || isClient) && dealerOptions.length === 1) setDealerId(dealerOptions[0].id);
  }, [open, isDealer, isClient, dealerOptions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const productById = (id: number) => products.data?.data.find((p) => p.id === id);
  const addLine = () => {
    if (!productId || !Number(qty)) return;
    setLines((ls) => [...ls.filter((l) => l.productId !== productId), { productId: Number(productId), quantity: Number(qty) }]);
    setProductId(''); setQty('1');
  };
  const total = lines.reduce((a, l) => { const p = productById(l.productId); return a + (p ? p.unitPrice * l.quantity * (1 + p.gstPercent / 100) : 0); }, 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>New Dealer Order</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField select label="Dealer" value={dealerId} onChange={(e) => setDealerId(Number(e.target.value))} required fullWidth disabled={isDealer}>
            {dealerOptions.map((d) => <MenuItem key={d.id} value={d.id}>{d.dealerName} ({d.dealerCode})</MenuItem>)}
          </TextField>
          {dealerId !== '' && !isClient && <DealerSummaryCard dealerId={Number(dealerId)} />}

          <Typography variant="subtitle2">Items</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField select size="small" label="Product" value={productId} onChange={(e) => setProductId(Number(e.target.value))} sx={{ minWidth: 260 }}>
              {(products.data?.data ?? []).map((p) => <MenuItem key={p.id} value={p.id}>{p.name} — {inr(p.unitPrice)}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Qty" type="number" value={qty} onChange={(e) => setQty(e.target.value)} sx={{ width: 100 }} />
            <Button startIcon={<Add />} onClick={addLine} disabled={!productId}>Add</Button>
          </Stack>

          <Table size="small">
            <TableHead><TableRow><TableCell>Product</TableCell><TableCell align="right">Unit</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Line Total</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>
              {lines.map((l) => {
                const p = productById(l.productId);
                const lineTotal = p ? p.unitPrice * l.quantity * (1 + p.gstPercent / 100) : 0;
                return (
                  <TableRow key={l.productId}>
                    <TableCell>{p?.name ?? `#${l.productId}`}</TableCell>
                    <TableCell align="right">{inr(p?.unitPrice)}</TableCell>
                    <TableCell align="right">{l.quantity}</TableCell>
                    <TableCell align="right">{inr(lineTotal)}</TableCell>
                    <TableCell align="right"><IconButton size="small" color="error" onClick={() => setLines((ls) => ls.filter((x) => x.productId !== l.productId))}><Delete fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                );
              })}
              {lines.length === 0 && <TableRow><TableCell colSpan={5}>No items added.</TableCell></TableRow>}
            </TableBody>
          </Table>
          <Typography align="right" fontWeight={700}>Grand Total (incl. GST): {inr(total)}</Typography>
          <TextField label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!dealerId || lines.length === 0}
          onClick={() => onSubmit({ dealerId: Number(dealerId), remarks: remarks || undefined, items: lines })}
        >
          Create Order
        </Button>
      </DialogActions>
    </Dialog>
  );
}
